export type Sink = {
    pos: number;
    view: DataView;
    littleEndian: boolean;
};

export const Sink = (
    arr: ArrayBuffer,
    pos: number = 0,
    littleEndian: boolean = true
): Sink => ({
    pos,
    littleEndian,
    view: new DataView(arr)
});

export type Serializer<T> = (sink: Sink, val: T) => Sink;
export type Deserializer<T> = (sink: Sink) => T;

const reserve = (sink: Sink, numberOfBytes: number): Sink => {
    const {
        view: { buffer },
        pos,
    } = sink;

    const curLen = buffer.byteLength;
    if (curLen - pos > numberOfBytes) return sink;

    const newLen = Math.max(curLen * 2, curLen + numberOfBytes);
    const newArr = new Uint8Array(newLen);
    newArr.set(new Uint8Array(buffer));
    return Sink(newArr.buffer, pos, sink.littleEndian);
};

export const write_u8: Serializer<number> = (sink, val) => {
    sink = reserve(sink, 1);
    const { view, pos } = sink;
    view.setUint8(pos, val);
    return (sink.pos += 1), sink;
};

export const write_u32: Serializer<number> = (sink, val) => {
    sink = reserve(sink, 4);
    const { view, pos, littleEndian } = sink;
    view.setUint32(pos, val, littleEndian);
    return (sink.pos += 4), sink;
};

export const write_u16: Serializer<number> = (sink, val) => {
    sink = reserve(sink, 2);
    const { view, pos, littleEndian } = sink;
    view.setUint16(pos, val, littleEndian);
    return (sink.pos += 2), sink;
};

function write_u64_unchecked(sink: Sink, val: bigint) {
    const { view, pos, littleEndian } = sink;
    view.setBigUint64(pos, val, littleEndian);
    return (sink.pos += 8), sink;
}

export const write_u64: Serializer<bigint> = (sink, val) =>
    write_u64_unchecked(reserve(sink, 8), val);

export const write_i64: Serializer<bigint> = (sink, val) => {
    sink = reserve(sink, 8);
    const { view, pos, littleEndian } = sink;
    view.setBigInt64(pos, val, littleEndian);
    return (sink.pos += 8), sink;
}

export const write_f32: Serializer<number> = (sink, val) => {
    sink = reserve(sink, 4);
    const { view, pos, littleEndian } = sink;
    view.setFloat32(pos, val, littleEndian);
    return (sink.pos += 4), sink;
};

export const write_f64: Serializer<number> = (sink, val) => {
    sink = reserve(sink, 8);
    const { view, pos, littleEndian } = sink;
    view.setFloat64(pos, val, littleEndian);
    return (sink.pos += 8), sink;
};

export const write_i32: Serializer<number> = (sink, val) => {
    sink = reserve(sink, 4);
    const { view, pos, littleEndian } = sink;
    view.setInt32(pos, val, littleEndian);
    return (sink.pos += 4), sink;
};

const encoder = new TextEncoder();

const encodeStrInto: (str: string, resArr: Uint8Array) => number =
    'encodeInto' in encoder
        ? (str, arr) => encoder.encodeInto(str, arr).written!
        : (str, arr) => {
            const bytes: Uint8Array = (encoder as any).encode(str);
            arr.set(bytes);
            return bytes.length;
        };

export const write_str: Serializer<string> = (sink, val) => {
    // reserve 8 bytes for the u64 len
    sink = reserve(sink, val.length * 3 + 8);
    const bytesWritten = encodeStrInto(
        val,
        new Uint8Array(sink.view.buffer, sink.pos + 8)
    );
    sink = write_u64_unchecked(sink, BigInt(bytesWritten));
    sink.pos += bytesWritten;
    return sink;
};

export const write_bool: Serializer<boolean> = (sink, val) =>
    write_u8(sink, val ? 1 : 0);

export const seq_writer = <T>(serEl: Serializer<T>): Serializer<T[]> => (
    sink,
    seq: T[]
) => seq.reduce(serEl, write_u64(sink, BigInt(seq.length)));

export const opt_writer = <T>(
    serEl: Serializer<T>
): Serializer<T | undefined> => (sink: Sink, val: T | undefined) =>
        val === undefined ? write_u8(sink, 0) : serEl(write_u8(sink, 1), val);

export const nullable_writer = <T>(
    serEl: Serializer<T>
): Serializer<T | null> => (sink: Sink, val: T | null) =>
        val === null ? write_u8(sink, 0) : serEl(write_u8(sink, 1), val);

// -------- Deserialization ----------

export const read_u8: Deserializer<number> = (sink) => {
    const { pos, view } = sink;
    return (sink.pos += 1), view.getUint8(pos);
};

export const skip_bytes = (sink: Sink, toSkip: number) => {
    return (sink.pos += toSkip), 0;
};

export const read_u16: Deserializer<number> = (sink) => {
    const { pos, view, littleEndian } = sink;
    return (sink.pos += 2), view.getUint16(pos, littleEndian);
};

export const read_u32: Deserializer<number> = (sink) => {
    const { pos, view, littleEndian } = sink;
    return (sink.pos += 4), view.getUint32(pos, littleEndian);
};

export const read_i64: Deserializer<bigint> = (sink) => {
    const { view, pos, littleEndian } = sink;

    // we don't support numbers more than u32 (yet)
    return (
        (sink.pos += 8), view.getBigInt64(pos, littleEndian)
    );
};
export const read_u64: Deserializer<bigint> = (sink) => {
    const { view, pos, littleEndian } = sink;

    // we don't support numbers more than u32 (yet)
    return (
        (sink.pos += 8), view.getBigUint64(pos, littleEndian)
    );
};

export const read_f32: Deserializer<number> = (sink) => {
    const { pos, view, littleEndian } = sink;
    return (sink.pos += 4), view.getFloat32(pos, littleEndian);
};

export const read_f64: Deserializer<number> = (sink) => {
    const { pos, view, littleEndian } = sink;
    return (sink.pos += 8), view.getFloat64(pos, littleEndian);
};

export const read_i32: Deserializer<number> = (sink) => {
    const { pos, view, littleEndian } = sink;
    return (sink.pos += 4), view.getInt32(pos, littleEndian);
};

export const read_bool: Deserializer<boolean> = (sink) => read_u8(sink) === 1;

export const opt_reader = <T>(
    readEl: Deserializer<T>
): Deserializer<T | undefined> => (sink) =>
        read_u8(sink) === 1 ? readEl(sink) : undefined;

export const nullable_reader = <T>(
    readEl: Deserializer<T>
): Deserializer<T | null> => (sink) =>
        read_u8(sink) === 1 ? readEl(sink) : null;

export const seq_reader = <T>(readEl: Deserializer<T>): Deserializer<T[]> => (
    sink
) => {
    const count = read_u64(sink);

    // Note it doesn't make sense to set capacity here
    // because it will mess up shapes
    const res = new Array<T>();

    for (let i = 0; i < count; i++) {
        res.push(readEl(sink));
    }

    return res;
};

export const is_sink_eof = (sink: Sink) => sink.pos >= sink.view.byteLength;

const decoder = new TextDecoder();

export const read_str: Deserializer<string> = (sink) => {
    const len = read_u64(sink);
    const {
        pos,
        view: { buffer },
    } = sink;
    const str = decoder.decode(new Uint8Array(buffer, pos, Number(len)));
    return (sink.pos += Number(len)), str;
};